import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { dataCurta, diasEntre, type Trilha } from "@noesis/shared";
import { useSessao } from "../estado/sessao.js";
import { FalhaDaApi, pedir } from "../api.js";
import { Icone } from "../componentes/Icone.js";
import { MenuDeAcoes } from "../componentes/Controles.js";
import { IconeConferido, IconeCriar, IconeExcluir } from "../componentes/icones.js";
import { useTrilhas } from "../estado/trilhas.js";

/**
 * Lista e gestão das trilhas — RN-04.
 *
 * A exclusão pede o nome digitado (invariante 11). A tela confere para dar
 * resposta imediata, mas quem decide é a API: `trilha.excluir` compara o nome
 * no servidor e recusa sem ele. A conferência da tela é conveniência; a da API
 * é a regra.
 */
export function Trilhas() {
  const { estado, recarregar, aplicar } = useTrilhas();
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [excluindo, setExcluindo] = useState<Trilha | null>(null);
  const navegar = useNavigate();

  async function acao(id: string, executar: () => Promise<void>) {
    setOcupado(id);
    setErro(null);
    try {
      await executar();
    } catch (e) {
      setErro(e instanceof FalhaDaApi ? e.message : "Não foi possível concluir a ação.");
    } finally {
      setOcupado(null);
    }
  }

  if (estado.nome === "carregando") {
    return (
      <Moldura>
        <div aria-busy="true">
          <span className="so-leitor" role="status">
            Carregando as trilhas
          </span>
          <div className="esqueleto esqueleto--faixa" />
        </div>
      </Moldura>
    );
  }

  if (estado.nome === "erro") {
    return (
      <Moldura>
        <div className="estado estado--erro" role="alert">
          <h2 className="estado__titulo">Não deu para carregar</h2>
          <p className="estado__texto">{estado.mensagem}</p>
          <button className="botao" type="button" onClick={() => void recarregar()}>
            Tentar de novo
          </button>
        </div>
      </Moldura>
    );
  }

  const emUso = estado.trilhas.filter((t) => !t.arquivada);
  const arquivadas = estado.trilhas.filter((t) => t.arquivada);

  return (
    <Moldura>
      {erro && (
        <p className="campo__erro" role="alert">
          {erro}
        </p>
      )}

      {estado.trilhas.length === 0 ? (
        <section className="estado">
          <h2 className="estado__titulo">Nenhuma trilha ainda</h2>
          <p className="estado__texto">
            Comece pela frente que você mais estuda. Dá para criar outras depois e trocar quando quiser.
          </p>
          <Link className="botao" to="/trilhas/nova">
            <Icone glifo={IconeCriar} papel="acao" />
            Criar a primeira
          </Link>
        </section>
      ) : (
        <>
          {(() => {
            const ativa = emUso.find((t) => t.id === estado.ativaId) ?? null;
            const outras = emUso.filter((t) => t.id !== estado.ativaId);
            const arquivar = (t: Trilha, arquivada: boolean) =>
              void acao(t.id, async () =>
                aplicar(await pedir("trilha.arquivar", { params: { id: t.id }, corpo: { arquivada } })),
              );
            return (
              <>
                {ativa && (
                  <section aria-labelledby="grupo-em-uso">
                    <h2 className="t-secao" id="grupo-em-uso">
                      Em uso
                    </h2>
                    <div className="trilha-mao trilha-mao--ativa cartao-mao">
                      <LinhaDaTrilha trilha={ativa} />
                      <div className="trilha-mao__acoes">
                        <Link className="botao botao--secundario" to={`/trilhas/${ativa.id}/editar`}>
                          <Icone glifo="editar" papel="acao" />
                          Editar
                        </Link>
                        <MenuDeAcoes
                          rotulo={`Mais ações de ${ativa.nome}`}
                          acoes={[
                            { rotulo: "Arquivar", aoEscolher: () => arquivar(ativa, true), desabilitada: ocupado === ativa.id },
                            { rotulo: "Excluir trilha…", destrutiva: true, aoEscolher: () => setExcluindo(ativa), desabilitada: ocupado === ativa.id },
                          ]}
                        />
                      </div>
                    </div>
                  </section>
                )}

                {(outras.length > 0 || arquivadas.length > 0) && (
                  <section aria-labelledby="grupo-outras">
                    <h2 className="t-secao" id="grupo-outras">
                      Outras trilhas
                    </h2>
                    <ul className="trilhas-lista">
                      {outras.map((t) => (
                        <li key={t.id} className="trilha-mao">
                          <LinhaDaTrilha trilha={t} />
                          <div className="trilha-mao__acoes">
                            <button
                              type="button"
                              className="link-mao"
                              disabled={ocupado === t.id}
                              onClick={() =>
                                void acao(t.id, async () => aplicar(await pedir("trilha.ativar", { params: { id: t.id } })))
                              }
                            >
                              <Icone glifo="arrow_right" papel="acao" tamanho="p" />
                              usar esta
                            </button>
                            <MenuDeAcoes
                              rotulo={`Mais ações de ${t.nome}`}
                              acoes={[
                                { rotulo: "Editar", aoEscolher: () => navegar(`/trilhas/${t.id}/editar`) },
                                { rotulo: "Arquivar", aoEscolher: () => arquivar(t, true), desabilitada: ocupado === t.id },
                                { rotulo: "Excluir trilha…", destrutiva: true, aoEscolher: () => setExcluindo(t), desabilitada: ocupado === t.id },
                              ]}
                            />
                          </div>
                        </li>
                      ))}
                      {arquivadas.map((t) => (
                        <li key={t.id} className="trilha-mao trilha-mao--arquivada">
                          <LinhaDaTrilha trilha={t} />
                          <div className="trilha-mao__acoes">
                            <button
                              type="button"
                              className="link-mao link-mao--tinta"
                              disabled={ocupado === t.id}
                              onClick={() => arquivar(t, false)}
                            >
                              desarquivar
                            </button>
                            <MenuDeAcoes
                              rotulo={`Mais ações de ${t.nome}`}
                              acoes={[
                                { rotulo: "Editar", aoEscolher: () => navegar(`/trilhas/${t.id}/editar`) },
                                { rotulo: "Excluir trilha…", destrutiva: true, aoEscolher: () => setExcluindo(t), desabilitada: ocupado === t.id },
                              ]}
                            />
                          </div>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {!ativa && outras.length === 0 && arquivadas.length > 0 && (
                  <p className="t-corpo">Todas as suas trilhas estão arquivadas. Desarquive uma para voltar a estudar.</p>
                )}

                <p className="t-ajuda">
                  Arquivar tira a trilha da troca rápida e guarda tudo. Excluir apaga itens, respostas e o caderno dela.
                </p>
              </>
            );
          })()}
        </>
      )}

      {excluindo && (
        <ConfirmarExclusao
          trilha={excluindo}
          aoCancelar={() => setExcluindo(null)}
          aoConfirmar={async (confirmacaoNome) => {
            aplicar(await pedir("trilha.excluir", { params: { id: excluindo.id }, corpo: { confirmacaoNome } }));
            setExcluindo(null);
          }}
        />
      )}
    </Moldura>
  );
}

function Moldura({ children }: { children: React.ReactNode }) {
  return (
    /*
      Sem título e sem "‹ Início" próprios: quem nomeia a tela é o cabeçalho de
      `Acervo`, e a frase desta seção virou a descrição da aba. O link de volta
      era de quando Trilhas era destino próprio — depois da fusão ele nascia
      **entre** a fita de abas e o título, fazendo as abas parecerem pertencer à
      home, e a navegação para o início já está na lateral e na faixa de baixo.
    */
    <div className="tela tela--ampla tela--painel">
      <div className="tela__corpo">
        {children}
      </div>
    </div>
  );
}

const DIAS_CURTOS = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];

/** "10 itens por dia · descanso dom e sáb · alvo 31/12/2026, faltam 98 dias". */
function metaLonga(t: Trilha, hoje: string): string {
  const partes = [`${t.metaDiaria} ${t.metaDiaria === 1 ? "item" : "itens"} por dia`];
  if (t.diasDeDescanso.length > 0) {
    const dias = [...t.diasDeDescanso].sort().map((d) => DIAS_CURTOS[d]!);
    partes.push(`descanso ${dias.length === 1 ? dias[0] : `${dias.slice(0, -1).join(", ")} e ${dias.at(-1)}`}`);
  }
  if (t.dataAlvo) {
    const faltam = diasEntre(hoje, t.dataAlvo);
    partes.push(faltam > 0 ? `alvo ${dataCurta(t.dataAlvo)}, faltam ${faltam} ${faltam === 1 ? "dia" : "dias"}` : `alvo ${dataCurta(t.dataAlvo)}`);
  }
  if (t.arquivada) partes.unshift("arquivada");
  return partes.join(" · ");
}

function LinhaDaTrilha({ trilha }: { trilha: Trilha }) {
  const { hoje } = useSessao();
  return (
    <div className="trilha-mao__linha">
      <span className={trilha.arquivada ? "carimbo carimbo--apagado" : "carimbo"} aria-hidden="true">
        {trilha.iniciais}
      </span>
      <div className="trilha-mao__texto">
        <p className="t-cartao trilha-mao__nome">{trilha.nome}</p>
        <p className="t-ajuda">{metaLonga(trilha, hoje)}</p>
        {trilha.descricao && <p className="t-ajuda trilha-mao__descricao">{trilha.descricao}</p>}
      </div>
    </div>
  );
}

/**
 * Confirmação por nome digitado (RN-25). O botão só destrava com o nome exato,
 * e o texto diz o que se perde — "tem certeza?" não informa nada.
 */
function ConfirmarExclusao({
  trilha,
  aoCancelar,
  aoConfirmar,
}: {
  trilha: Trilha;
  aoCancelar: () => void;
  aoConfirmar: (nome: string) => Promise<void>;
}) {
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const confere = texto.trim() === trilha.nome.trim();

  async function confirmar() {
    setEnviando(true);
    setErro(null);
    try {
      await aoConfirmar(texto);
    } catch (e) {
      setErro(e instanceof FalhaDaApi ? e.message : "Não foi possível excluir.");
      setEnviando(false);
    }
  }

  return (
    <section className="cartao pilha confirmacao" role="alertdialog" aria-labelledby="excluir-titulo" aria-modal="false">
      <h2 className="estado__titulo" id="excluir-titulo">
        Excluir “{trilha.nome}”
      </h2>
      <p className="estado__texto">
        Isso apaga a trilha e tudo que está dentro dela: itens, respostas, notas do caderno e histórico. Não tem
        como desfazer.
      </p>
      <p className="estado__texto">
        Se a ideia é só parar de estudar essa frente por enquanto, <strong>arquivar</strong> guarda tudo e some da
        lista.
      </p>

      <div className="campo">
        <label className="campo__rotulo" htmlFor="confirmacao">
          Para confirmar, digite <span className="dado">{trilha.nome}</span>
        </label>
        <input
          id="confirmacao"
          className="campo__entrada"
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          autoComplete="off"
          autoFocus
        />
      </div>

      {erro && (
        <p className="campo__erro" role="alert">
          {erro}
        </p>
      )}

      <div className="linha">
        <button className="botao botao--secundario" type="button" onClick={aoCancelar} disabled={enviando}>
          Cancelar
        </button>
        <button className="botao botao--destrutivo" type="button" onClick={() => void confirmar()} disabled={!confere || enviando}>
          {enviando ? "Excluindo…" : "Excluir para sempre"}
        </button>
      </div>
    </section>
  );
}
